import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mail } from 'lucide-react';
import { useAuth } from '@/lib/auth';

const ForgotPassword: React.FC = () => {
  const { requestPasswordReset } = useAuth();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await requestPasswordReset(email);
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to send password reset email');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md bg-white rounded-2xl border border-[#E8E8E8] shadow-[0_20px_70px_-28px_rgba(0,0,0,0.14)] p-7 sm:p-9">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-semibold font-poppins text-[#1A1A1A]">Reset password</h1>
          <p className="text-[#555555] mt-2 text-sm">
            Enter your account email and we&apos;ll send a password reset link.
          </p>
        </div>

        {submitted ? (
          <div className="space-y-4">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
              Reset email sent to <span className="font-medium">{email}</span>. Check your inbox to continue.
            </div>
            <Link
              to="/login"
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#121212] py-3 text-sm font-semibold text-white hover:bg-[#2A2A2A] transition-colors"
            >
              Back to sign in
              <ArrowRight size={16} />
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="forgot-email" className="block text-sm font-medium text-[#555555] mb-1.5">
                Email
              </label>
              <div className="relative">
                <Mail
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#A3A3A3] pointer-events-none"
                  size={18}
                  strokeWidth={1.75}
                />
                <input
                  id="forgot-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                  className="w-full pl-10 pr-4 py-3 text-[15px] text-[#1A1A1A] placeholder:text-[#A3A3A3] bg-[#FAFAFA] border border-[#E0E0E0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#121212]/20 focus:border-[#121212] transition-shadow"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            {error && <div className="text-sm text-red-700 bg-red-50 border border-red-100/80 rounded-xl px-4 py-3">{error}</div>}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#121212] text-white py-3.5 rounded-xl text-[15px] font-semibold hover:bg-[#2A2A2A] disabled:opacity-50 disabled:pointer-events-none transition-colors"
            >
              {isSubmitting ? 'Sending reset link...' : 'Send reset link'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
